import { createSelector } from 'reselect';
import {
  getHomeSpacesForView,
  getHomeViewRole,
  getUserRoleInSpace,
} from '@app/selectors/home';
import { currentUserId } from '@core/selectors/user';

import { getMySpaces } from '@core/selectors/space/space.selectors';

export const getHomeRecentSpaces = createSelector(
  getHomeSpacesForView,
  (state: any) => state.spaceSelector?.recentlyViewedSpaces?.spaceList || [],
  getHomeViewRole,
  currentUserId,
  (state: any) => state,
  (spacesForView, recentSpaceList, viewRole, userId, state) => {
    // Wait until the user's assigned spaces have loaded.
    // If we process this before they load, we can't reliably determine space roles.
    if (!state.data?.hasInitialData) {
      return [];
    }

    const spacesForViewMap = new Map(
      (spacesForView || []).map((s: any) => [s.objectId || s.id, s])
    );
    const mySpaces = getMySpaces(state) || {};

    return recentSpaceList
      .filter((recentSpace: any) => {
        const isAssignedSpace = !!mySpaces[recentSpace.spaceId];

        // If the space is not in the user's assigned spaces (mySpaces),
        // it means it's a staff-access space. Staff spaces show for both roles (role === null).
        if (!isAssignedSpace) {
          return true;
        }

        // For assigned spaces, we can now reliably get their role.
        const role = getUserRoleInSpace(state, recentSpace.spaceId, userId);
        return role === viewRole || role === null;
      })
      .map((recentSpace: any) => {
        const space = spacesForViewMap.get(recentSpace.spaceId);
        const isAssignedSpace = !!mySpaces[recentSpace.spaceId];
        const allSpaces = state.data?.classRooms || {};
        const fallbackSpace = allSpaces[recentSpace.spaceId];

        if (!space) {
          return {
            id: recentSpace.spaceId,
            objectId: recentSpace.spaceId,
            name: fallbackSpace?.name || recentSpace.name,
            avatar: fallbackSpace?.avatar || recentSpace.avatar,
            startDate: !isAssignedSpace
              ? null
              : fallbackSpace?.startDate || recentSpace.startDate,
            endDate: !isAssignedSpace
              ? null
              : fallbackSpace?.endDate || recentSpace.endDate,
            isStarred: fallbackSpace
              ? !!fallbackSpace.isStarred
              : !!recentSpace.isStarred,
            lastNavigation: recentSpace.lastNavigation,
            isStaffAccess: !isAssignedSpace,
            isAssigned: isAssignedSpace,
            archived:
              fallbackSpace?.archived ?? (recentSpace.archived || false),
          };
        }

        return {
          ...space,
          startDate: !isAssignedSpace ? null : space.startDate,
          endDate: !isAssignedSpace ? null : space.endDate,
          isStarred: !!space.isStarred,
          lastNavigation: recentSpace.lastNavigation,
          isStaffAccess: !isAssignedSpace,
          isAssigned: isAssignedSpace,
          archived: recentSpace.archived || space.archived || false,
        };
      });
  }
);
